import React, { createContext, useReducer } from "react";
import { initialBoardState } from "../utils/init-state";
import { initialTeamTableState } from "../utils/init-team-table-state";
import sessionState from "../utils/session-init-state";
import DecisionSheet from "../utils/decision-sheet";
import StrategyPaper from "../utils/strategy-paper";
import AnnualReport from "../utils/annual-report-init-state";
import produce from "immer";

const initialState = {
  Board: initialBoardState,
  TeamTable: initialTeamTableState,
  Session: sessionState,
  strategyPaper: StrategyPaper,
  decisionSheet: DecisionSheet,
  annualReport: AnnualReport,
};

export const StateContext = createContext();
export const DispatchContext = createContext();

const refactorCoins = (coins) => {
  let newCoins = [];

  if (coins.length > 0) {
    let coinsValue = coins.reduce((total, num) => {
      return parseFloat(total) + parseFloat(num);
    });

    while (coinsValue > 0) {
      if (coinsValue >= 10) {
        newCoins.push(10);
        coinsValue -= 10;
      } else if (coinsValue >= 1) {
        newCoins.push(1);
        coinsValue -= 1;
      } else {
        newCoins.push(0.5);
        coinsValue -= 0.5;
      }
    }
  }

  return newCoins;
};

function refactorSum(coins) {
  var coinsList = [];
  while (coins > 0) {
    if (coins >= 10) {
      coinsList.push(10);
      coins -= 10;
    } else if (coins >= 1) {
      coinsList.push(1);
      coins -= 1;
    } else {
      coinsList.push(0.5);
      coins -= 0.5;
    }
  }
  return coinsList;
}
function getTotalLiquidity(paper) {
  let total = 0;

  // First section
  paper.first.forEach(c => {
    if (c.Liquidity) total += Number(c.Liquidity) || 0;
  });

  // Second section - Account for buy/sell
  paper.second.forEach(c => {
    if (c.Liquidity) total += Number(c.Liquidity) || 0;

    // Also calculate based on buy/sell actions
    c.categories.forEach(category => {
      category.subCategories.forEach(subCat => {
        subCat.fields.forEach(field => {
          if (field.action === "buy" && field.amount > 0) {
            // Add cost for buying
            // You'll need to define the cost per item based on field.name (P/E)
            const cost = calculateCost(field.name, field.amount);
            total += cost;
          } else if (field.action === "sell" && field.amount > 0) {
            // Subtract revenue for selling (or add negative cost)
            const revenue = calculateRevenue(field.name, field.amount);
            total -= revenue; // Selling generates cash
          }
        });
      });
    });
  });

  // ... rest of the function

  return total;
}

// Helper function to calculate costs
function calculateCost(itemType, quantity) {
  const costs = {
    "P": 10, // Property cost
    "E": 5   // Equipment cost
  };
  return costs[itemType] * quantity || 0;
}

function calculateRevenue(itemType, quantity) {
  // Selling might get different revenue (e.g., 80% of cost)
  const resaleRate = 0.8;
  return calculateCost(itemType, quantity) * resaleRate;
}

function reducer(draft, action) {
  switch (action.type) {
    case "RestoreTeamTable":
      draft.TeamTable = action.data;
      return;
    case "RestoreBoard":
      draft.Board = action.data;
      return;
    case "RestoreMsgs":
      draft.Session.messages = action.data;
      return;
    case "RestoreForm":
      if (action.data) {
        if (action.data.strategyPaper) {
          draft.strategyPaper = action.data.strategyPaper.strategyPaper || action.data.strategyPaper;

          // Migrate existing data to include action and amount fields
          if (draft.strategyPaper.second && draft.strategyPaper.second.length > 0) {
            draft.strategyPaper.second.forEach(company => {
              if (company.categories) {
                company.categories.forEach(category => {
                  if (category.subCategories) {
                    category.subCategories.forEach(subCat => {
                      if (subCat.fields) {
                        subCat.fields.forEach(field => {
                          // If field doesn't have action/amount, add them
                          if (!field.hasOwnProperty('action')) {
                            field.action = field.value ? "buy" : "none";
                          }
                          if (!field.hasOwnProperty('amount')) {
                            field.amount = field.value ? 1 : 0;
                          }
                        });
                      }
                    });
                  }
                });
              }
            });
          }

          // Ensure credits object exists
          if (!draft.strategyPaper.credits) {
            draft.strategyPaper.credits = {
              fivePercent: 0,
              tenPercent: 0,
              drewRepay: 0,
              newCreditStatus: 0
            };
          }
        }
        if (action.data.decisionSheet) {
          draft.decisionSheet = action.data.decisionSheet.decisionSheet || action.data.decisionSheet;
        }
        if (action.data.annualReport) {
          draft.annualReport = action.data.annualReport.annualReport || action.data.annualReport;
        }
      }
      return;
    case "changeFormYear":
      if (action.data.form) {
        draft[action.data.type] = action.data.form[action.data.type];
      } else {
        draft.decisionSheet = JSON.parse(JSON.stringify(DecisionSheet));
        draft.annualReport = JSON.parse(JSON.stringify(AnnualReport));
      draft.strategyPaper = JSON.parse(JSON.stringify(StrategyPaper));
    if (!draft.strategyPaper.credits) {
      draft.strategyPaper.credits = {
        fivePercent: 0,
        tenPercent: 0,
        drewRepay: 0,
        newCreditStatus: 0
      };
    }
  }
  draft.Session.formSelectedYear = action.data.year;
  return;
    case "changeYear":
      if (action.year !== undefined) {
        draft.Session.formSelectedYear = action.year;
        draft.Session.year = action.year;
      }

      return;
    case "RestoreLaborMarket":
      draft.LaborMarket = action.data;
      return;
    case "LaborMarket":
      draft.LaborMarket[action.data.region][action.data.key] = action.data.value;
      return;
    case "UpdateTeamTable":
      draft.TeamTable[action.data.type] = action.data.value;
      return;
    case "UpdateRegionCustomerData":
      draft.Board.regions[action.data.region].customer[action.data.key] = action.data.value;
      return;
    case "UpdateBoardValue":
      console.log(action.type);
      draft.Board.rightBlock[action.data.type] = action.data.value;
      return;
    case "DecreaseCash":
      let containers = draft.Board.cash.map((x) => {
        let cashCoins = 0;
        cashCoins = x.coins.reduce((a, b) => a + b, 0);
        return cashCoins;
      });

      let index = 0;
      let count = action.data.value;
      while (count > 0) {
        if (containers[index] >= count) {
          let coinsList = draft.Board.cash[index].coins;
          coinsList = refactorSum(containers[index] - count);
          draft.Board.cash[index].coins = coinsList;
          count = 0;
        } else {
          count -= containers[index];
          draft.Board.cash[index].coins = [];
        }
        index++;
      }

      return;
    case "IncreaseCash":
      let containerIndex = 0;
      let valueCount = action.data.value;
      let firstContainer = draft.Board.cash[containerIndex];
      let coinsList = draft.Board.cash[containerIndex].coins;

      if (firstContainer.hasContainer) {
        var coins = firstContainer.coins.reduce((a, b) => a + b, 0) + action.data.value;
        coinsList = refactorSum(coins);
        while (valueCount > 0) {
          if (coinsList.length < 20) {
            draft.Board.cash[containerIndex].coins = coinsList;
            valueCount = 0;
          }
          containerIndex++;
        }
      }

      return;
    case "updateRightBlock":
      console.log("here");
      draft.Board.rightBlock.depreciation.map((item, itemIndex) => {
        if (item.coins.length > 0) {
          item.coins = [];
          item.hasContainer = false;
        }
      });
      draft.Board.rightBlock.costOfSales.map((item, itemIndex) => {
        if (item.coins.length > 0) {
          item.coins = [];
          item.hasContainer = false;
        }
      });
      return;
    case "DecreaseTeamTable":
      console.log(action.data.value);
      draft.TeamTable[action.data.type] -= action.data.value;
      return;
    case "IncreaseTeamTable":
      console.log(action.data);
      draft.TeamTable[action.data.type] += action.data.value;
      if (action.data.type === "token5") {
        draft.Board.leftBlock.credit5.value = 0;
      }
      if (action.data.type === "token10") {
        draft.Board.leftBlock.credit10.value = 0;
      }
            if (action.data.type === "blacktoken") {
        draft.Board.leftBlock.blacktoken.value = 0;
      }
      return;
    case "toggleChat":
      draft.Session.isChatOpened = action.isOpened;
      return;
    case "messagesList":
      var message = action.message;
      draft.Session.messages.push(message);
      return;
    case "addEquity":
      draft.Board.leftBlock.equity = action.value;
      return;
    case "decisionSheet":
      draft.Session["formStatus"].decisionSheet = action.value;
      return;
    case "updateDecisionSheet":
      draft.decisionSheet[action.data.regionIndex].services[action.data.serviceIndex][
        action.data.itemIndex
      ].value = action.data.value;

      return;
    case "strategyPaper":
      draft.Session["formStatus"].strategyPaper = action.value;
      return;
// In the reducer function, add a new case for handling credit changes:
case "updateStrategyPaper":
  if (action.data.alias === "fourth") {
    if (action.data.liquidityIndex !== undefined) {
      draft.strategyPaper[action.data.alias].Liquidity[action.data.liquidityIndex].value =
        action.data.value;
    } else {
      draft.strategyPaper[action.data.alias].fields[action.data.fieldIndex].value =
        action.data.value;
    }
  }

  // Handle second section buy/sell updates - FIXED CONDITION
  if (
    action.data.alias === "second" &&
    action.data.fieldIndex !== undefined &&
    action.data.value &&
    typeof action.data.value === 'object'
  ) {
    draft.strategyPaper[action.data.alias][action.data.companyIndex]
      .categories[action.data.categoryIndex]
      .subCategories[action.data.subCategoryIndex]
      .fields[action.data.fieldIndex] = action.data.value;
  }

  // Handle credit fields
  if (action.data.alias === "credits") {
    // Initialize credits object if it doesn't exist
    if (!draft.strategyPaper.credits) {
      draft.strategyPaper.credits = {
        fivePercent: 0,
        tenPercent: 0,
        drewRepay: 0,
        newCreditStatus: 0
      };
    }

    if (action.data.field) {
      draft.strategyPaper.credits[action.data.field] = action.data.value;

      // Auto-calculate new credit status when drewRepay changes
      if (action.data.field === "drewRepay") {
        const fivePercent = draft.strategyPaper.credits.fivePercent || 0;
        const tenPercent = draft.strategyPaper.credits.tenPercent || 0;
        const drewRepay = action.data.value || 0;
        draft.strategyPaper.credits.newCreditStatus = fivePercent + tenPercent + drewRepay;
      }

      // Also recalculate newCreditStatus if fivePercent or tenPercent changes
      if (action.data.field === "fivePercent" || action.data.field === "tenPercent") {
        const fivePercent = draft.strategyPaper.credits.fivePercent || 0;
        const tenPercent = draft.strategyPaper.credits.tenPercent || 0;
        const drewRepay = draft.strategyPaper.credits.drewRepay || 0;
        draft.strategyPaper.credits.newCreditStatus = fivePercent + tenPercent + drewRepay;
      }
    }
  }

  if (
    action.data.categoryIndex === undefined &&
    action.data.subCategoryIndex === undefined &&
    action.data.alias !== "third" &&
    action.data.alias !== "fourth" &&
    action.data.alias !== "credits"
  ) {
    draft.strategyPaper[action.data.alias][action.data.companyIndex].Liquidity =
      action.data.value;
  }
  if (
    action.data.categoryIndex !== undefined &&
    action.data.subCategoryIndex === undefined &&
    action.data.fieldIndex === undefined
  ) {
    draft.strategyPaper[action.data.alias][action.data.companyIndex].categories[
      action.data.categoryIndex
    ].value = action.data.value;
  }
  if (
    action.data.fieldIndex !== undefined &&
    action.data.liquidityIndex === undefined &&
    action.data.alias !== "fourth" &&
    action.data.alias !== "credits" &&
    action.data.alias !== "second"  // Exclude second section as it's handled above
  ) {
    draft.strategyPaper[action.data.alias][action.data.companyIndex].categories[
      action.data.categoryIndex
    ].subCategories[action.data.subCategoryIndex].fields[action.data.fieldIndex].value =
      action.data.value;
  }
  if (
    action.data.fieldIndex === undefined &&
    action.data.categoryIndex === undefined &&
    action.data.liquidityIndex !== undefined &&
    action.data.alias !== "fourth" &&
    action.data.alias !== "credits"
  ) {
    draft.strategyPaper[action.data.alias][action.data.companyIndex].Liquidity[
      action.data.liquidityIndex
    ].value = action.data.value;
  }

  // 🔥 Recalculate the total liquidity every time something changes
  const total = getTotalLiquidity(draft.strategyPaper);
  const lastIndex = draft.strategyPaper.fourth.Liquidity.length - 1;
  draft.strategyPaper.fourth.Liquidity[lastIndex].value = total;

  return;
    case "annualReport":
      draft.Session["formStatus"].annualReport = action.value;
      return;
    case "updateAnnualReport":
      draft.annualReport[action.data.groupName][action.data.field] = action.data.value;
      return;
    case "addContainer":
      if (action.index !== undefined) {
        if (action.serviceIndex !== undefined) {
          draft.Board.regions[action.regionIndex].services[action.serviceIndex][
            action.index
          ].hasContainer = true;
          draft.Board.regions[action.regionIndex].services[action.serviceIndex][
            action.index
          ].coins = action.coins;
        } else {
          if (action.hub) {
            draft.Board[action.branch][action.alias].containers[action.index].hasContainer = true;
            draft.Board[action.branch][action.alias].containers[action.index].coins = action.coins;
          } else {
            draft.Board[action.branch][action.alias][action.index].hasContainer = true;
            draft.Board[action.branch][action.alias][action.index].coins = action.coins;
          }
        }
      } else {
        draft.Board[action.branch][action.alias].hasContainer = true;
        draft.Board[action.branch][action.alias].coins = action.coins;
      }

      return;
    case "removeContainer":
      if (action.index !== undefined) {
        if (action.serviceIndex !== undefined) {
          draft.Board.regions[action.regionIndex].services[action.serviceIndex][
            action.index
          ].hasContainer = false;
          draft.Board.regions[action.regionIndex].services[action.serviceIndex][
            action.index
          ].coins = [];
        } else {
          if (action.hub) {
            draft.Board[action.branch][action.alias].containers[action.index].hasContainer = false;
            draft.Board[action.branch][action.alias].containers[action.index].coins = [];
          } else {
            draft.Board[action.branch][action.alias][action.index].hasContainer = false;
            draft.Board[action.branch][action.alias][action.index].coins = [];
          }
        }
      } else {
        draft.Board[action.branch][action.alias].hasContainer = false;
        draft.Board[action.branch][action.alias].coins = [];
      }
      return;
    case "deleteContainer":
      let sum = action.coins.reduce((a, b) => a + b, 0);
      draft.TeamTable.coins += sum;
      return;
    case "addCoin":
      if (action.fieldIndex !== undefined) {
        if (action.serviceIndex !== undefined) {
          let coinList = [
            ...draft.Board.regions[action.regionIndex].services[action.serviceIndex][
              action.fieldIndex
            ].coins,
          ];
          coinList.push(action.value);
          coinList = refactorCoins(coinList);
          draft.Board.regions[action.regionIndex].services[action.serviceIndex][
            action.fieldIndex
          ].coins = coinList;
        } else {
          if (action.hub) {
            let coinList = [
              ...draft.Board[action.branch][action.alias].containers[action.fieldIndex].coins,
            ];
            coinList.push(action.value);
            coinList = refactorCoins(coinList);
            draft.Board[action.branch][action.alias].containers[action.fieldIndex].coins = coinList;
          } else {
            let coinList = [...draft.Board[action.branch][action.alias][action.fieldIndex].coins];
            coinList.push(action.value);
            coinList = refactorCoins(coinList);
            draft.Board[action.branch][action.alias][action.fieldIndex].coins = coinList;
          }
        }
      } else {
        let coinList = [...draft.Board[action.branch][action.alias].coins];
        coinList.push(action.value);
        coinList = refactorCoins(coinList);
        draft.Board[action.branch][action.alias].coins = coinList;
      }

      return;
    case "removeCoin":
      if (action.fieldIndex !== undefined) {
        if (action.serviceIndex !== undefined) {
          let coinList = [
            ...draft.Board.regions[action.regionIndex].services[action.serviceIndex][
              action.fieldIndex
            ].coins,
          ];
          coinList.splice(action.index, 1);
          coinList = refactorCoins(coinList);
          draft.Board.regions[action.regionIndex].services[action.serviceIndex][
            action.fieldIndex
          ].coins = coinList;
        } else {
          if (action.hub) {
            let coinList = [
              ...draft.Board[action.branch][action.alias].containers[action.fieldIndex].coins,
            ];
            coinList.splice(action.index, 1);
            coinList = refactorCoins(coinList);
            draft.Board[action.branch][action.alias].containers[action.fieldIndex].coins = coinList;
          } else {
            let coinList = [...draft.Board[action.branch][action.alias][action.fieldIndex].coins];
            coinList.splice(action.index, 1);
            coinList = refactorCoins(coinList);
            draft.Board[action.branch][action.alias][action.fieldIndex].coins = coinList;
          }
        }
      } else {
        let coinList = [...draft.Board[action.branch][action.alias].coins];
        coinList.splice(action.index, 1);
        coinList = refactorCoins(coinList);
        draft.Board[action.branch][action.alias].coins = coinList;
      }

      return;
    case "addToken":
      if (action.tokenType === 5) {
        draft.Board.leftBlock.credit5.checked = true;
        draft.Board.leftBlock.credit5.value = action.value;
      }
else if (String(action.tokenType).toLowerCase() === "blacktoken" || action.tokenType === "BLACK_TOKEN"){
   draft.Board.leftBlock.blacktoken.checked = true;
   draft.Board.leftBlock.blacktoken.value = action.value;
}
      else {
        draft.Board.leftBlock.credit10.checked = true;
        draft.Board.leftBlock.credit10.value = action.value;
      }
      return;
    case "deleteToken":
      if (action.tokenType === 5) {
        draft.Board.leftBlock.credit5.checked = false;
      }
else if (String(action.tokenType).toLowerCase() === "blacktoken" || action.tokenType === "BLACK_TOKEN"){
   draft.Board.leftBlock.blacktoken.checked = false;

}
      else {
        draft.Board.leftBlock.credit10.checked = false;
      }
      return;

    case "addAvatar":
      draft.Board.regions[action.regionIndex].employees[action.serviceIndex][action.alias][
        action.index
      ].checked = true;
      return;
    case "addPin":
      if (action.regionIndex !== undefined) {
        draft.Board.regions[action.regionIndex].pins[action.index].checked = true;
      }
      if (action.serviceIndex !== undefined) {
        draft.Board.leftBlock.services[action.serviceIndex].pins[action.index].checked = true;
      }
      if (action.alias !== undefined) {
        draft.Board.leftBlock[action.alias].pins[action.index].checked = true;
      }
      return;

    case "deletePin":
      if (action.regionIndex !== undefined) {
        draft.Board.regions[action.regionIndex].pins[action.index].checked = false;
      }
      if (action.serviceIndex !== undefined) {
        draft.Board.leftBlock.services[action.serviceIndex].pins[action.index].checked = false;
      }
      if (action.alias !== undefined) {
        draft.Board.leftBlock[action.alias].pins[action.index].checked = false;
      }
      return;
    case "deleteAvatar":
      draft.Board.regions[action.regionIndex].employees[action.serviceIndex][action.alias][
        action.index
      ].checked = false;
      return;

      case "addFiles":
        if (action.files && action.files.file && action.files.name) {
          draft.Session.files.push(action.files);
        }
        return;
    case "removeFiles":
      let list = draft.Session.files.filter((file) => file._id !== action.files._id);
      draft.Session.files = list;
      return;
    case "files":
      draft.Session.files = action.files;
      return;

    case "locationStatus":
      if (action.index !== undefined) {
        if (action.serviceIndex !== undefined) {
          draft.Board.regions[action.regionIndex].services[action.serviceIndex][
            action.index
          ].rented = action.value;
          draft.Board.regions[action.regionIndex].services[action.serviceIndex][
            action.index
          ].coins = action.coins;
        } else {
          if (action.hub) {
            draft.Board[action.branch][action.alias].containers[action.index].rented = action.value;
            draft.Board[action.branch][action.alias].containers[action.index].coins = action.coins;
          } else {
            draft.Board[action.branch][action.alias][action.index].rented = action.value;
            draft.Board[action.branch][action.alias][action.index].coins = action.coins;
          }
        }
      } else {
        draft.Board[action.branch][action.alias].rented = action.value;
        draft.Board[action.branch][action.alias].coins = action.coins;
      }

      return;
    case "containerDragging":
      if (action.regionIndex !== undefined) {
        draft.Board.regions[action.regionIndex][action.alias][action.id].isObjectDragging =
          action.isDragging ? true : false;
        draft.Board.regions[action.regionIndex][action.alias][action.id].draggingObject =
          "CONTAINER";
        draft.Board.regions[action.regionIndex][action.alias][action.id].playerId =
          action.isDragging ? action.playerId : null;
      } else {
        draft.Board[action.alias][action.id].isObjectDragging = action.isDragging ? true : false;
        draft.Board[action.alias][action.id].draggingObject = "CONTAINER";
        draft.Board[action.alias][action.id].playerId = action.isDragging ? action.playerId : null;
      }
      return;
    case "coinDragging":
      if (action.regionIndex !== undefined) {
        draft.Board.regions[action.regionIndex][action.alias][action.id].isObjectDragging =
          action.isDragging ? true : false;
        draft.Board.regions[action.regionIndex][action.alias][action.id].draggingObject = "COIN";
        draft.Board.regions[action.regionIndex][action.alias][action.id].playerId =
          action.isDragging ? action.playerId : null;
        draft.Board.regions[action.regionIndex][action.alias][action.id].coinIndex =
          action.coinIndex;
      } else {
        draft.Board[action.alias][action.id].isObjectDragging = action.isDragging ? true : false;
        draft.Board[action.alias][action.id].draggingObject = "COIN";
        draft.Board[action.alias][action.id].playerId = action.isDragging ? action.playerId : null;
        draft.Board[action.alias][action.id].coinIndex = action.coinIndex;
      }
      return;
    default:
      return;
  }
}

const curriedReducer = produce(reducer);
export const StateProvider = ({ children }) => {
  const [state, dispatch] = useReducer(curriedReducer, initialState);

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </DispatchContext.Provider>
  );
};
